import React, { useState, useEffect } from 'react';
import { ViewToggle, ViewType } from '@/components/marketplace/ViewToggle';
import { CreatorEditDialog } from '@/components/admin/CreatorEditDialog';
import { CreatorAddDialog } from '@/components/admin/CreatorAddDialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useAPISportsData } from '@/hooks/useAPISportsData';
import { useESPNAthletesData } from '@/hooks/useESPNAthletesData';
import { useGroupedCreatorsData, GroupedCreator } from '@/hooks/useGroupedCreatorsData';
import { useToast } from '@/hooks/use-toast';
import { useDebounce } from '@/hooks/useDebounce';
import { Download, CheckCircle, XCircle, Clock, RefreshCw, Users, Trophy, ArrowLeft, Search, ChevronLeft, ChevronRight, ExternalLink, Trash2, Edit, X, Camera, Image, Sparkles, GraduationCap, Database, Video, Upload, Link, Unlink, ChevronDown, ChevronUp, UserPlus, ImageIcon } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { AthletePhotoUploadDialog } from './AthletePhotoUploadDialog';
import { CreatorImportModal } from './CreatorImportModal';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';
import { getAthleteFullName, getAthleteInitials } from '@/utils/athleteUtils';
import { getProxiedImageUrl } from '@/utils/imageProxy';
// NCAA Sports configuration
const NCAA_SPORTS = [
  { key: 'cfb', label: 'College Football', icon: '🏈' },
  { key: 'mcbb', label: "Men's Basketball", icon: '🏀' },
  { key: 'wcbb', label: "Women's Basketball", icon: '🏀' },
  { key: 'college-baseball', label: 'College Baseball', icon: '⚾' },
  { key: 'college-softball', label: 'College Softball', icon: '🥎' },
];

// Major NCAA Conferences by sport
const NCAA_CONFERENCES: Record<string, Array<{ id: string; name: string }>> = {
  'cfb': [
    { id: '80', name: 'SEC' },
    { id: '4', name: 'ACC' },
    { id: '5', name: 'Big Ten' },
    { id: '12', name: 'Big 12' },
    { id: '9', name: 'Pac-12' },
    { id: '151', name: 'American' },
    { id: '17', name: 'Mountain West' },
    { id: '18', name: 'Sun Belt' },
    { id: '15', name: 'MAC' },
    { id: '12', name: 'Conference USA' },
  ],
  'mcbb': [
    { id: '8', name: 'SEC' },
    { id: '2', name: 'ACC' },
    { id: '7', name: 'Big Ten' },
    { id: '4', name: 'Big 12' },
    { id: '21', name: 'Big East' },
    { id: '62', name: 'American' },
    { id: '44', name: 'Mountain West' },
    { id: '46', name: 'West Coast' },
  ],
  'wcbb': [
    { id: '8', name: 'SEC' },
    { id: '2', name: 'ACC' },
    { id: '7', name: 'Big Ten' },
    { id: '4', name: 'Big 12' },
    { id: '21', name: 'Big East' },
  ],
  'college-baseball': [
    { id: '8', name: 'SEC' },
    { id: '2', name: 'ACC' },
    { id: '7', name: 'Big Ten' },
    { id: '4', name: 'Big 12' },
  ],
  'college-softball': [
    { id: '8', name: 'SEC' },
    { id: '2', name: 'ACC' },
    { id: '7', name: 'Big Ten' },
    { id: '4', name: 'Big 12' },
  ],
};

export const APISportsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [reviewingClaim, setReviewingClaim] = useState<any>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [photoUploadAthlete, setPhotoUploadAthlete] = useState<any>(null);
  
  // Image sync state
  const [syncingImages, setSyncingImages] = useState(false);
  
  // ESPN sync state
  const [syncingESPN, setSyncingESPN] = useState(false);
  const [espnSyncProgress, setEspnSyncProgress] = useState<{ processed: number; updated: number; failed: number } | null>(null);
  
  // ESPN Marketplace batch sync state
  const [espnMarketplaceSyncing, setEspnMarketplaceSyncing] = useState<string | null>(null);
  const [espnMarketplaceProgress, setEspnMarketplaceProgress] = useState<{
    sport: string;
    totalAthletes: number;
    totalProcessed: number;
    athletesImported: number;
    progressPercent: number;
    hasMore: boolean;
    jobId: string | null;
    nextOffset: number | null;
  } | null>(null);
  
  // Tab control
  const [activeTab, setActiveTab] = useState('espn-marketplace');
  
  // ESPN Athletes tab filters
  const [espnAthleteSportFilter, setEspnAthleteSportFilter] = useState('');
  const [espnAthleteSearch, setEspnAthleteSearch] = useState('');
  const [espnAthleteClaimFilter, setEspnAthleteClaimFilter] = useState('all');
  const [espnAthleteTeamFilter, setEspnAthleteTeamFilter] = useState('all');
  const [espnAthletePage, setEspnAthletePage] = useState(0);
  
  // ESPN edit mode
  const [isEspnEditMode, setIsEspnEditMode] = useState(false);
  const [selectedEspnAthletes, setSelectedEspnAthletes] = useState<Set<string>>(new Set());
  const [espnAthleteToDelete, setEspnAthleteToDelete] = useState<{ id: string; name: string } | null>(null);
  const [espnBulkDeleteDialogOpen, setEspnBulkDeleteDialogOpen] = useState(false);
  
  // Delete All dialogs
  const [deleteAllEspnDialogOpen, setDeleteAllEspnDialogOpen] = useState(false);
  
  // ESPN Marketplace pro sport selection
  const [selectedProSport, setSelectedProSport] = useState('');
  const [isBackfillingCollege, setIsBackfillingCollege] = useState(false);
  
  // ESPN individual athlete search & sync
  const [espnAthleteSearchTerm, setEspnAthleteSearchTerm] = useState('');
  const [espnAthleteSearchResults, setEspnAthleteSearchResults] = useState<Array<{
    espnId: number;
    name: string;
    firstName: string | null;
    lastName: string | null;
    team: string | null;
    sport: string | null;
    league: string | null;
    position: string | null;
    headshot: string | null;
    sportKey: string | null;
    status: string | null;
  }>>([]);
  const [searchingEspnAthlete, setSearchingEspnAthlete] = useState(false);
  const [syncingSingleAthlete, setSyncingSingleAthlete] = useState<number | null>(null);
  
  // NCAA Sports sync state
  const [selectedNCAASport, setSelectedNCAASport] = useState('');
  const [selectedConference, setSelectedConference] = useState('');
  const [ncaaSyncing, setNcaaSyncing] = useState(false);
  const [ncaaSyncProgress, setNcaaSyncProgress] = useState<{
    sport: string;
    conference?: string;
    totalAthletes: number;
    totalProcessed: number;
    athletesImported: number;
    progressPercent: number;
    hasMore: boolean;
    jobId: string | null;
    nextOffset: number | null;
    teamsCount: number;
  } | null>(null);
  
  // Creators sync state
  const [creatorsPlatform, setCreatorsPlatform] = useState<'tiktok' | 'instagram' | 'youtube'>('tiktok');
  const [creatorsSearchQuery, setCreatorsSearchQuery] = useState('');
  const [creatorsResults, setCreatorsResults] = useState<any[]>([]);
  const [isSearchingCreators, setIsSearchingCreators] = useState(false);
  const [isSyncingCreators, setIsSyncingCreators] = useState(false);
  const [selectedCreatorsToSync, setSelectedCreatorsToSync] = useState<Set<string>>(new Set());
  const [fetchDemographics, setFetchDemographics] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{ current: number; total: number } | null>(null);
  
  // Creator import modal state
  const [isCreatorImportModalOpen, setIsCreatorImportModalOpen] = useState(false);
  
  // Single creator add state
  const [isAddSingleCreatorOpen, setIsAddSingleCreatorOpen] = useState(false);
  
  // Creator database browser state
  const [creatorDbPlatformFilter, setCreatorDbPlatformFilter] = useState('');
  const [creatorDbSearch, setCreatorDbSearch] = useState('');
  const [creatorDbClaimFilter, setCreatorDbClaimFilter] = useState('all');
  const [creatorDbPage, setCreatorDbPage] = useState(0);
  const [isCreatorEditMode, setIsCreatorEditMode] = useState(false);
  const [selectedDbCreators, setSelectedDbCreators] = useState<Set<string>>(new Set());
  const [creatorToDelete, setCreatorToDelete] = useState<{ id: string; name: string; isGrouped: boolean } | null>(null);
  const [expandedCreatorGroups, setExpandedCreatorGroups] = useState<Set<string>>(new Set());
  const [creatorBulkDeleteDialogOpen, setCreatorBulkDeleteDialogOpen] = useState(false);
  const [deleteAllCreatorsDialogOpen, setDeleteAllCreatorsDialogOpen] = useState(false);
  const [creatorDbLinkFilter, setCreatorDbLinkFilter] = useState('all');
  const [creatorViewType, setCreatorViewType] = useState<ViewType>('grid');
  const [creatorToEdit, setCreatorToEdit] = useState<GroupedCreator | null>(null);
  const [showSearchSync, setShowSearchSync] = useState(false);
  
  // Unlinked accounts dropdown state
  const [showUnlinkedList, setShowUnlinkedList] = useState(false);
  const [unlinkedCreatorsList, setUnlinkedCreatorsList] = useState<any[]>([]);
  const [loadingUnlinkedList, setLoadingUnlinkedList] = useState(false);
  
  // Link creator accounts state
  const [isLinkingCreators, setIsLinkingCreators] = useState(false);
  const [resyncingAvatars, setResyncingAvatars] = useState(false);
  const [linkingProgress, setLinkingProgress] = useState<{
    linked: number;
    failed: number;
    notFound: number;
    total: number;
    percent: number;
    currentBatch: number;
    totalBatches: number;
  } | null>(null);
  
  const debouncedEspnSearch = useDebounce(espnAthleteSearch, 300);

  const { toast } = useToast();
  const { 
    claimRequests, 
    claimsLoading, 
    refetchClaims,
    approveClaim,
    approvingClaim,
    creatorClaimRequests,
    creatorClaimsLoading,
    refetchCreatorClaims,
    approveCreatorClaim,
    approvingCreatorClaim,
  } = useAPISportsData();

  // ESPN Athletes Data Hook
  const {
    espnAthleteStats,
    espnStatsLoading,
    espnAthletes,
    espnAthletesTotal,
    espnAthletesLoading,
    espnAthleteFilters,
    setEspnAthleteFilters,
    espnUniqueTeams,
    refetchEspnAthletes,
    deleteEspnAthlete,
    deletingEspnAthlete,
    deleteEspnAthletes,
    deletingEspnAthletes,
    deleteAllEspnAthletesBySport,
    deletingAllEspnAthletes,
  } = useESPNAthletesData();

  // Creators Database Hook (Grouped)
  const debouncedCreatorDbSearch = useDebounce(creatorDbSearch, 300);
  const {
    creatorGroups: dbCreatorGroups,
    total: dbCreatorsTotal,
    stats: creatorStats,
    isLoading: dbCreatorsLoading,
    refetch: refetchDbCreators,
    deleteCreatorGroup,
    bulkDeleteGroups,
    deleteAllCreators,
  } = useGroupedCreatorsData({
    platform: creatorDbPlatformFilter,
    searchTerm: debouncedCreatorDbSearch,
    claimStatus: creatorDbClaimFilter,
    offset: creatorDbPage * 20,
  });
  
  const deletingCreator = deleteCreatorGroup.isPending;
  const deletingCreators = bulkDeleteGroups.isPending;
  const deletingAllCreators = deleteAllCreators.isPending;
  // ESPN Filters - Update filters when local state changes
  useEffect(() => {
    setEspnAthleteFilters(prev => ({
      ...prev,
      sport: espnAthleteSportFilter,
      searchTerm: debouncedEspnSearch,
      claimStatus: espnAthleteClaimFilter,
      team: espnAthleteTeamFilter,
      offset: espnAthletePage * 20,
    }));
  }, [espnAthleteSportFilter, debouncedEspnSearch, espnAthleteClaimFilter, espnAthleteTeamFilter, espnAthletePage, setEspnAthleteFilters]);

  // Reset ESPN page when filters change
  useEffect(() => {
    setEspnAthletePage(0);
  }, [espnAthleteSportFilter, debouncedEspnSearch, espnAthleteClaimFilter, espnAthleteTeamFilter]);

  // Reset ESPN team filter when sport changes
  useEffect(() => {
    setEspnAthleteTeamFilter('all');
  }, [espnAthleteSportFilter]);

  // Reset creator page when filters change
  useEffect(() => {
    setCreatorDbPage(0);
  }, [creatorDbPlatformFilter, debouncedCreatorDbSearch, creatorDbClaimFilter]);

  // Reset creator page when filters change
  useEffect(() => {
    setCreatorDbPage(0);
  }, [creatorDbPlatformFilter, debouncedCreatorDbSearch, creatorDbClaimFilter]);

  // Fetch unlinked creators when dropdown is toggled open
  useEffect(() => {
    if (showUnlinkedList) {
      const fetchUnlinkedList = async () => {
        setLoadingUnlinkedList(true);
        const { data } = await supabase
          .from('creators')
          .select('id, username, platform, avatar_url, display_name')
          .eq('is_linked', false)
          .order('platform')
          .limit(100);
        setUnlinkedCreatorsList(data || []);
        setLoadingUnlinkedList(false);
      };
      fetchUnlinkedList();
    }
  }, [showUnlinkedList]);

  const handleApproveClaim = async (approved: boolean) => {
    if (!reviewingClaim) return;

    if (reviewingClaim._claimType === 'creator') {
      // Approve/reject all linked accounts for this user
      const allCreators = reviewingClaim._allCreators || [reviewingClaim];
      const pendingCreators = allCreators.filter((c: any) => c.claim_status === 'pending');
      for (const creator of pendingCreators) {
        approveCreatorClaim({
          creatorId: creator.id,
          approved,
          adminNotes: adminNotes || undefined,
        });
      }
    } else {
      approveClaim({
        claimRequestId: reviewingClaim.id,
        approved,
        adminNotes: adminNotes || undefined,
      });
    }

    setReviewingClaim(null);
    setAdminNotes('');
  };

  const handleSyncESPN = async (sport: string = 'nba', forceRefresh: boolean = false) => {
    setSyncingESPN(true);
    setEspnSyncProgress(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to sync ESPN data',
          variant: 'destructive',
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('sync-espn-data', {
        body: { sport, limit: 100, forceRefresh },
      });

      if (error) {
        throw error;
      }

      setEspnSyncProgress({
        processed: data.processed,
        updated: data.updated,
        failed: data.failed,
      });

      toast({
        title: 'ESPN Enhancement Complete',
        description: `Enhanced ${data.updated} athletes with ESPN data. ${data.failed} failed.`,
      });

      refetchEspnAthletes();
    } catch (error) {
      console.error('ESPN sync error:', error);
      toast({
        title: 'ESPN Sync Failed',
        description: error instanceof Error ? error.message : 'Failed to sync ESPN data',
        variant: 'destructive',
      });
    } finally {
      setSyncingESPN(false);
    }
  };

  // Search ESPN for individual athlete - client-side to bypass server IP blocks
  const handleSearchEspnAthlete = async () => {
    if (!espnAthleteSearchTerm.trim() || espnAthleteSearchTerm.trim().length < 2) {
      toast({ title: 'Enter at least 2 characters', variant: 'destructive' });
      return;
    }
    setSearchingEspnAthlete(true);
    setEspnAthleteSearchResults([]);
    try {
      const { searchESPNAthletes } = await import('@/utils/espnSearch');
      const results = await searchESPNAthletes(espnAthleteSearchTerm.trim());
      setEspnAthleteSearchResults(results);
      if (!results.length) {
        toast({ title: 'No Results', description: 'No athletes found matching that name.' });
      }
    } catch (err: any) {
      console.error('ESPN athlete search error:', err);
      toast({ title: 'Search Failed', description: err.message || 'Could not search ESPN', variant: 'destructive' });
    } finally {
      setSearchingEspnAthlete(false);
    }
  };

  // Sync a single ESPN athlete by ID
  const handleSyncSingleEspnAthlete = async (espnId: number, sportKey: string, athleteName: string) => {
    setSyncingSingleAthlete(espnId);
    try {
      const { data, error } = await supabase.functions.invoke('sync-espn-athletes', {
        body: { sport: sportKey, athleteId: espnId },
      });
      if (error) throw error;
      if (data?.success) {
        toast({ title: 'Athlete Synced', description: `${athleteName} has been synced to the ESPN database.` });
        refetchEspnAthletes();
      } else {
        toast({ title: 'Sync Failed', description: data?.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (err: any) {
      console.error('Single athlete sync error:', err);
      toast({ title: 'Sync Failed', description: err.message || 'Could not sync athlete', variant: 'destructive' });
    } finally {
      setSyncingSingleAthlete(null);
    }
  };

  // ESPN Marketplace batch sync with continuation
  const handleESPNMarketplaceSync = async (sport: string, isContinuation: boolean = false) => {
    setEspnMarketplaceSyncing(sport);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to sync ESPN data',
          variant: 'destructive',
        });
        return;
      }

      // Prepare request body
      const requestBody: any = { 
        sport, 
        limit: 1000,
        batchSize: 500,
      };
      
      // If continuing, include previous job info
      if (isContinuation && espnMarketplaceProgress) {
        requestBody.offset = espnMarketplaceProgress.nextOffset;
        requestBody.jobId = espnMarketplaceProgress.jobId;
      }

      toast({ 
        title: `${isContinuation ? 'Continuing' : 'Starting'} ${sport.toUpperCase()} sync...`, 
        description: `Processing batch of 500 athletes...` 
      });

      const { data, error } = await supabase.functions.invoke('sync-espn-athletes', {
        body: requestBody,
      });

      if (error) {
        throw error;
      }

      // Update progress state
      const newProgress = {
        sport,
        totalAthletes: data.totalAthletes,
        totalProcessed: data.totalProcessed,
        athletesImported: (isContinuation && espnMarketplaceProgress ? espnMarketplaceProgress.athletesImported : 0) + data.athletesImported,
        progressPercent: data.progressPercent,
        hasMore: data.hasMore,
        jobId: data.jobId,
        nextOffset: data.nextOffset,
      };
      
      setEspnMarketplaceProgress(newProgress);

      if (data.hasMore) {
        toast({
          title: 'Batch Complete',
          description: `Processed ${data.totalProcessed}/${data.totalAthletes} athletes (${data.progressPercent}%). Click "Continue Sync" to process more.`,
        });
      } else {
        toast({
          title: 'ESPN Sync Complete',
          description: `Successfully imported ${newProgress.athletesImported} ${sport.toUpperCase()} athletes.`,
        });
        refetchEspnAthletes();
      }
    } catch (error) {
      console.error('ESPN marketplace sync error:', error);
      toast({
        title: 'ESPN Sync Failed',
        description: error instanceof Error ? error.message : 'Failed to sync ESPN data',
        variant: 'destructive',
      });
    } finally {
      setEspnMarketplaceSyncing(null);
    }
  };

  // Handle backfilling college data for existing pro athletes
  const handleBackfillCollege = async (sport: string) => {
    setIsBackfillingCollege(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to backfill data',
          variant: 'destructive',
        });
        return;
      }

      toast({ 
        title: `Backfilling college data for ${sport.toUpperCase()}...`, 
        description: 'Resolving college names from ESPN API...' 
      });

      let totalUpdated = 0;
      let hasMore = true;
      let offset = 0;

      // Process in batches until complete
      while (hasMore) {
        const { data, error } = await supabase.functions.invoke('backfill-college-data', {
          body: { sport, batchSize: 50, offset },
        });

        if (error) {
          throw error;
        }

        totalUpdated += data.updated || 0;
        hasMore = data.hasMore;
        offset = data.nextOffset;

        if (hasMore) {
          toast({
            title: 'Processing...',
            description: `Updated ${totalUpdated} athletes. ${data.remainingWithoutCollege} remaining...`,
          });
        }
      }

      toast({
        title: 'College Backfill Complete',
        description: `Successfully updated ${totalUpdated} ${sport.toUpperCase()} athletes with college data.`,
      });
      
      refetchEspnAthletes();
    } catch (error) {
      console.error('College backfill error:', error);
      toast({
        title: 'Backfill Failed',
        description: error instanceof Error ? error.message : 'Failed to backfill college data',
        variant: 'destructive',
      });
    } finally {
      setIsBackfillingCollege(false);
    }
  };

  // NCAA Sports batch sync with conference filtering
  const handleNCAASportsSync = async (sport: string, conferenceId?: string, isContinuation: boolean = false) => {
    setNcaaSyncing(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to sync NCAA data',
          variant: 'destructive',
        });
        return;
      }

      const requestBody: any = { 
        sport, 
        batchSize: 500,
      };
      
      // Add conference filter if selected
      if (conferenceId && conferenceId !== 'all') {
        requestBody.conferenceId = conferenceId;
      }
      
      // If continuing, include previous job info
      if (isContinuation && ncaaSyncProgress) {
        requestBody.offset = ncaaSyncProgress.nextOffset;
        requestBody.jobId = ncaaSyncProgress.jobId;
      }

      const sportLabel = NCAA_SPORTS.find(s => s.key === sport)?.label || sport;
      const confName = conferenceId && conferenceId !== 'all' 
        ? NCAA_CONFERENCES[sport]?.find(c => c.id === conferenceId)?.name 
        : null;

      toast({ 
        title: `${isContinuation ? 'Continuing' : 'Starting'} ${sportLabel} sync...`, 
        description: confName ? `Syncing ${confName} conference...` : 'Processing all conferences...'
      });

      const { data, error } = await supabase.functions.invoke('sync-espn-athletes', {
        body: requestBody,
      });

      if (error) {
        throw error;
      }

      const newProgress = {
        sport,
        conference: data.conference || confName,
        totalAthletes: data.totalAthletes,
        totalProcessed: data.totalProcessed,
        athletesImported: (isContinuation && ncaaSyncProgress ? ncaaSyncProgress.athletesImported : 0) + data.athletesImported,
        progressPercent: data.progressPercent,
        hasMore: data.hasMore,
        jobId: data.jobId,
        nextOffset: data.nextOffset,
        teamsCount: data.teamsCount || 0,
      };
      
      setNcaaSyncProgress(newProgress);

      if (data.hasMore) {
        toast({
          title: 'Batch Complete',
          description: `Processed ${data.totalProcessed}/${data.totalAthletes} athletes (${data.progressPercent}%). Click "Continue Sync" to process more.`,
        });
      } else {
        toast({
          title: 'NCAA Sync Complete',
          description: `Successfully imported ${newProgress.athletesImported} ${sportLabel} athletes from ${newProgress.teamsCount} teams.`,
        });
        refetchEspnAthletes();
      }
    } catch (error) {
      console.error('NCAA sync error:', error);
      toast({
        title: 'NCAA Sync Failed',
        description: error instanceof Error ? error.message : 'Failed to sync NCAA data',
        variant: 'destructive',
      });
    } finally {
      setNcaaSyncing(false);
    }
  };

  // Handle creators search
  const handleCreatorsSearch = async () => {
    if (!creatorsSearchQuery.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a search term',
        variant: 'destructive',
      });
      return;
    }
    
    setIsSearchingCreators(true);
    setCreatorsResults([]);
    
    try {
      const { data, error } = await supabase.functions.invoke('search-creators', {
        body: { platform: creatorsPlatform, query: creatorsSearchQuery },
      });

      if (error) {
        throw error;
      }

      setCreatorsResults(data.creators || []);
      toast({
        title: 'Search Complete',
        description: `Found ${data.creators?.length || 0} creators on ${creatorsPlatform}`,
      });
    } catch (error) {
      console.error('Creators search error:', error);
      toast({
        title: 'Search Failed',
        description: error instanceof Error ? error.message : 'Failed to search creators',
        variant: 'destructive',
      });
    } finally {
      setIsSearchingCreators(false);
    }
  };

  // Handle syncing selected creators to database
  const handleSyncCreators = async () => {
    if (selectedCreatorsToSync.size === 0) {
      toast({
        title: 'Error',
        description: 'Please select creators to sync',
        variant: 'destructive',
      });
      return;
    }
    
    setIsSyncingCreators(true);
    const creatorsToSync = creatorsResults.filter(c => selectedCreatorsToSync.has(c.id));
    setSyncProgress({ current: 0, total: creatorsToSync.length });
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      // Fetch full profile for each creator and insert into database
      for (let i = 0; i < creatorsToSync.length; i++) {
        const creator = creatorsToSync[i];
        setSyncProgress({ current: i + 1, total: creatorsToSync.length });
        
        try {
          // Fetch detailed profile from ScrapeCreators API
          const { data: profileData, error: profileError } = await supabase.functions.invoke('fetch-creator-profile', {
            body: { 
              platform: creator.platform, 
              username: creator.username,
              fetchDemographics: fetchDemographics && creator.platform === 'tiktok'
            }
          });
          
          if (profileError) {
            console.error('Error fetching creator profile:', profileError);
            // Fall back to basic data if profile fetch fails
          }
          
          const detailedProfile = profileData?.profile || {};
          
          // Merge search results with detailed profile
          const { error: upsertError } = await supabase
            .from('creators')
            .upsert({
              platform: creator.platform,
              platform_id: creator.id,
              username: detailedProfile.username || creator.username,
              display_name: detailedProfile.display_name || creator.display_name,
              avatar_url: detailedProfile.avatar_url || creator.avatar_url,
              follower_count: detailedProfile.follower_count || creator.follower_count,
              following_count: detailedProfile.following_count || creator.following_count,
              video_count: detailedProfile.video_count || creator.video_count,
              like_count: detailedProfile.like_count || creator.like_count,
              is_verified: detailedProfile.is_verified ?? creator.is_verified,
              bio: detailedProfile.bio || creator.bio,
              // New enhanced fields
              external_links: detailedProfile.external_links || [],
              audience_demographics: detailedProfile.audience_demographics || {},
              category: detailedProfile.category || null,
              region: detailedProfile.region || null,
              email: detailedProfile.email || null,
              business_category: detailedProfile.business_category || null,
              profile_url: detailedProfile.profile_url || null,
              last_synced_at: new Date().toISOString(),
            }, {
              onConflict: 'platform,platform_id',
            });
            
          if (upsertError) {
            console.error('Error syncing creator:', upsertError);
            failCount++;
          } else {
            successCount++;
          }
        } catch (creatorError) {
          console.error('Error processing creator:', creatorError);
          failCount++;
        }
      }
      
      toast({
        title: 'Sync Complete',
        description: `Successfully synced ${successCount} creators${failCount > 0 ? `, ${failCount} failed` : ''}`,
      });
      
      setSelectedCreatorsToSync(new Set());
    } catch (error) {
      console.error('Creators sync error:', error);
      toast({
        title: 'Sync Failed',
        description: error instanceof Error ? error.message : 'Failed to sync creators',
        variant: 'destructive',
      });
    } finally {
      setIsSyncingCreators(false);
      setSyncProgress(null);
    }
  };

  // Handle linking unlinked creator accounts with batch processing
  const handleLinkUnlinkedCreators = async () => {
    const BATCH_SIZE = 10;
    const BATCH_TIMEOUT = 25000;

    setIsLinkingCreators(true);

    try {
      // Fetch all unlinked creator IDs
      let query = supabase
        .from('creators')
        .select('id')
        .eq('is_linked', false);

      if (creatorDbPlatformFilter) {
        query = query.eq('platform', creatorDbPlatformFilter);
      }

      const { data: unlinkedCreators, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      if (!unlinkedCreators || unlinkedCreators.length === 0) {
        toast({ title: 'No unlinked creators', description: 'All creators are already linked.' });
        setIsLinkingCreators(false);
        return;
      }

      const creatorIds = unlinkedCreators.map(c => c.id);
      const totalBatches = Math.ceil(creatorIds.length / BATCH_SIZE);
      let totalLinked = 0;
      let totalFailed = 0;
      let totalNotFound = 0;

      setLinkingProgress({
        linked: 0, failed: 0, notFound: 0,
        total: creatorIds.length, percent: 0,
        currentBatch: 0, totalBatches,
      });

      for (let i = 0; i < creatorIds.length; i += BATCH_SIZE) {
        const batch = creatorIds.slice(i, i + BATCH_SIZE);
        const currentBatchNum = Math.floor(i / BATCH_SIZE) + 1;

        try {
          const result = await Promise.race([
            supabase.functions.invoke('link-creator-accounts', {
              body: { creatorIds: batch },
            }),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Batch timeout')), BATCH_TIMEOUT)
            ),
          ]);

          const data = (result as any).data;
          const error = (result as any).error;
          if (error) throw error;

          totalLinked += data?.linked || 0;
          totalFailed += data?.failed || 0;
          totalNotFound += data?.notFound || 0;

          setLinkingProgress({
            linked: totalLinked, failed: totalFailed, notFound: totalNotFound,
            total: creatorIds.length,
            percent: Math.round((currentBatchNum / totalBatches) * 100),
            currentBatch: currentBatchNum, totalBatches,
          });

          if (data?.apiCreditsExhausted) {
            toast({ title: 'API Credits Exhausted', description: 'Stopped linking due to API credit limit.', variant: 'destructive' });
            break;
          }
        } catch (err) {
          console.error(`Batch ${currentBatchNum} error:`, err);
          totalFailed += batch.length;
          setLinkingProgress({
            linked: totalLinked, failed: totalFailed, notFound: totalNotFound,
            total: creatorIds.length,
            percent: Math.round((currentBatchNum / totalBatches) * 100),
            currentBatch: currentBatchNum, totalBatches,
          });
          if (err instanceof Error && err.message !== 'Batch timeout') break;
        }
      }

      // --- Set creator group avatars from linked creators ---
      try {
        const { data: linkedWithAvatars } = await supabase
          .from('creators')
          .select('id, creator_group_id, platform, avatar_url')
          .eq('is_linked', true)
          .not('avatar_url', 'is', null)
          .not('creator_group_id', 'is', null);

        if (linkedWithAvatars && linkedWithAvatars.length > 0) {
          const groupMap: Record<string, { platform: string; avatar_url: string }[]> = {};
          for (const c of linkedWithAvatars) {
            if (!c.creator_group_id || !c.avatar_url) continue;
            if (!groupMap[c.creator_group_id]) groupMap[c.creator_group_id] = [];
            groupMap[c.creator_group_id].push({ platform: c.platform, avatar_url: c.avatar_url });
          }

          const platformPriority = ['tiktok', 'instagram', 'youtube'];
          for (const [groupId, creators] of Object.entries(groupMap)) {
            let chosenAvatar = creators[0]?.avatar_url;
            for (const p of platformPriority) {
              const match = creators.find(c => c.platform === p);
              if (match) { chosenAvatar = match.avatar_url; break; }
            }
            if (chosenAvatar) {
              await supabase
                .from('creator_groups')
                .update({ avatar_url: chosenAvatar })
                .eq('id', groupId);
            }
          }
        }
      } catch (avatarErr) {
        console.error('Failed to set group avatars:', avatarErr);
      }

      toast({
        title: 'Linking Complete',
        description: `Linked ${totalLinked} of ${creatorIds.length} creators.${totalNotFound > 0 ? ` ${totalNotFound} not found.` : ''}${totalFailed > 0 ? ` ${totalFailed} failed.` : ''}`,
        variant: totalLinked > 0 ? 'default' : 'destructive',
      });

      refetchDbCreators();
    } catch (error) {
      console.error('Link creators error:', error);
      toast({ title: 'Linking Failed', description: error instanceof Error ? error.message : 'Failed to link creators', variant: 'destructive' });
    } finally {
      setIsLinkingCreators(false);
      setTimeout(() => setLinkingProgress(null), 3000);
    }
  };

  const getStatusBadge = (status: string, errorMessage?: string | null) => {
    const variants: Record<string, any> = {
      pending: { variant: 'secondary', icon: Clock },
      in_progress: { variant: 'default', icon: RefreshCw },
      completed: { variant: 'default', icon: CheckCircle },
      failed: { variant: 'destructive', icon: XCircle },
    };
    
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    const badge = (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
    
    if (status === 'failed' && errorMessage) {
      return (
        <div className="flex flex-col gap-1">
          {badge}
          <span className="text-xs text-destructive line-clamp-2" title={errorMessage}>
            {errorMessage}
          </span>
        </div>
      );
    }
    
    return badge;
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div>
        <div className="flex items-center gap-4 mb-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/profile')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">API Sports Management</h1>
          </div>
        </div>
        <p className="text-muted-foreground">
          Import and manage verified athlete data from ESPN
        </p>
      </div>

      {/* Pending Claims Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pending Claims</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {claimsLoading || creatorClaimsLoading ? '...' : 
                (claimRequests?.filter(c => c.status === 'pending').length || 0) + 
                (creatorClaimRequests?.filter(c => c.claim_status === 'pending').length || 0)
              }
            </div>
            <p className="text-xs text-muted-foreground mt-1">Awaiting review</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full overflow-x-auto">
          <TabsTrigger value="espn-marketplace" className="flex-shrink-0">Pro Sports</TabsTrigger>
          <TabsTrigger value="ncaa-sports" className="flex-shrink-0">
            <GraduationCap className="h-4 w-4 mr-1" />
            NCAA Sports
          </TabsTrigger>
          <TabsTrigger value="creators" className="flex-shrink-0">
            <Video className="h-4 w-4 mr-1" />
            Creators
          </TabsTrigger>
          <TabsTrigger value="espn-athletes" className="flex-shrink-0">ESPN Athletes</TabsTrigger>
          <TabsTrigger value="claims" className="flex-shrink-0">Claim Requests</TabsTrigger>
        </TabsList>

        {/* ESPN Marketplace Tab - Separate ESPN Data Source */}
        <TabsContent value="espn-marketplace" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20">
                  <Trophy className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <CardTitle>ESPN Marketplace Sync</CardTitle>
                  <CardDescription>
                    Import athletes directly from ESPN's free API into the separate ESPN Marketplace. 
                    Uses batch processing to handle large rosters without timeout.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Individual Athlete Search & Sync */}
              <div className="rounded-lg border border-border p-4 space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Search &amp; Sync Individual Athlete
                </h4>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search by name or paste ESPN URL (e.g. espn.com/nba/player/_/id/6462/...)"
                    value={espnAthleteSearchTerm}
                    onChange={(e) => setEspnAthleteSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchEspnAthlete()}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSearchEspnAthlete}
                    disabled={searchingEspnAthlete || espnAthleteSearchTerm.trim().length < 2}
                    variant="secondary"
                  >
                    {searchingEspnAthlete ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="ml-2">Search ESPN</span>
                  </Button>
                </div>
                
                {/* Search Results */}
                {espnAthleteSearchResults.length > 0 && (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {espnAthleteSearchResults.map((result) => (
                      <div
                        key={result.espnId}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 bg-background"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar className="h-10 w-10 flex-shrink-0">
                            {result.headshot && <AvatarImage src={result.headshot} alt={result.name} />}
                            <AvatarFallback className="text-xs">
                              {result.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">{result.name}</p>
                              {result.status && (
                                <Badge
                                  variant={result.status.toLowerCase() === 'active' ? 'default' : 'secondary'}
                                  className={`text-[10px] px-1.5 py-0 ${
                                    result.status.toLowerCase() === 'active'
                                      ? 'bg-green-600 text-white hover:bg-green-700'
                                      : ''
                                  }`}
                                >
                                  {result.status}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {result.team && <span>{result.team}</span>}
                              {result.position && <span>• {result.position}</span>}
                              {result.league && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  {result.league}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          disabled={!result.sportKey || syncingSingleAthlete === result.espnId}
                          onClick={() => {
                            if (result.sportKey) {
                              handleSyncSingleEspnAthlete(result.espnId, result.sportKey, result.name);
                            }
                          }}
                        >
                          {syncingSingleAthlete === result.espnId ? (
                            <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <Download className="h-3 w-3 mr-1" />
                          )}
                          Sync
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {espnAthleteSearchResults.length === 0 && !searchingEspnAthlete && espnAthleteSearchTerm.length > 0 && (
                  <p className="text-xs text-muted-foreground">No results yet. Click "Search ESPN" to find athletes.</p>
                )}
              </div>

              {/* Progress Display */}
              {espnMarketplaceProgress && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <RefreshCw className={`h-4 w-4 ${espnMarketplaceSyncing ? 'animate-spin' : ''}`} />
                      {espnMarketplaceSyncing ? 'Syncing' : 'Sync Progress'}: {espnMarketplaceProgress.sport.toUpperCase()}
                    </h4>
                    <Badge variant={espnMarketplaceProgress.hasMore ? 'secondary' : 'default'}>
                      {espnMarketplaceProgress.hasMore ? 'In Progress' : 'Complete'}
                    </Badge>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-muted rounded-full h-3">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all duration-300"
                      style={{ width: `${espnMarketplaceProgress.progressPercent}%` }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">{espnMarketplaceProgress.totalProcessed}</p>
                      <p className="text-xs text-muted-foreground">Processed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-500">{espnMarketplaceProgress.athletesImported}</p>
                      <p className="text-xs text-muted-foreground">Imported</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{espnMarketplaceProgress.totalAthletes}</p>
                      <p className="text-xs text-muted-foreground">Total Athletes</p>
                    </div>
                  </div>
                  
                  {espnMarketplaceProgress.hasMore && !espnMarketplaceSyncing && (
                    <Button 
                      onClick={() => handleESPNMarketplaceSync(espnMarketplaceProgress.sport, true)}
                      className="w-full"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Continue Sync ({espnMarketplaceProgress.totalAthletes - espnMarketplaceProgress.totalProcessed} remaining)
                    </Button>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {['nba', 'nfl', 'mlb', 'nhl', 'wnba', 'mls'].map((sport) => (
                  <Button
                    key={sport}
                    variant={selectedProSport === sport ? 'default' : 'outline'}
                    className="h-auto py-4 flex flex-col gap-1"
                    disabled={espnMarketplaceSyncing !== null}
                    onClick={() => {
                      setSelectedProSport(sport);
                      setEspnMarketplaceProgress(null);
                    }}
                  >
                    {espnMarketplaceSyncing === sport ? (
                      <RefreshCw className="h-5 w-5 animate-spin mb-1" />
                    ) : null}
                    <span className="text-lg font-bold uppercase">{sport}</span>
                    <span className="text-xs text-muted-foreground">
                      {espnMarketplaceSyncing === sport ? 'Syncing...' : 'Click to select'}
                    </span>
                  </Button>
                ))}
              </div>
              
              {/* Sync Button */}
              {selectedProSport && !espnMarketplaceProgress && (
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleESPNMarketplaceSync(selectedProSport, false)}
                    disabled={espnMarketplaceSyncing !== null || isBackfillingCollege}
                    className="flex-1"
                    size="lg"
                  >
                    {espnMarketplaceSyncing ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Sync {selectedProSport.toUpperCase()} Athletes from ESPN
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => handleBackfillCollege(selectedProSport)}
                    disabled={espnMarketplaceSyncing !== null || isBackfillingCollege}
                    variant="outline"
                    size="lg"
                  >
                    {isBackfillingCollege ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Backfilling...
                      </>
                    ) : (
                      <>
                        <Database className="mr-2 h-4 w-4" />
                        Backfill College Data
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <h4 className="font-medium mb-2">About ESPN Marketplace Sync</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Batch Processing:</strong> Syncs 500 athletes per batch for faster syncing</li>
                  <li>• <strong>Resumable:</strong> Click "Continue Sync" if interrupted</li>
                  <li>• <strong>Progress Tracking:</strong> Real-time progress display</li>
                  <li>• Free ESPN API - no API key required</li>
                  <li>• Includes college, draft info, experience, and HD photos</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NCAA Sports Tab */}
        <TabsContent value="ncaa-sports" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-green-500/20 to-blue-500/20">
                  <GraduationCap className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <CardTitle>NCAA Collegiate Athlete Sync</CardTitle>
                  <CardDescription>
                    Sync collegiate athletes by sport and conference from ESPN's free API.
                    Use conference filtering to manage the large number of teams and athletes.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Sport Selection */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Select NCAA Sport</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {NCAA_SPORTS.map((sport) => (
                    <Button
                      key={sport.key}
                      variant={selectedNCAASport === sport.key ? 'default' : 'outline'}
                      onClick={() => {
                        setSelectedNCAASport(sport.key);
                        setSelectedConference('');
                        setNcaaSyncProgress(null);
                      }}
                      className="h-auto py-3 flex flex-col gap-1"
                    >
                      <span className="text-xl">{sport.icon}</span>
                      <span className="text-sm font-medium">{sport.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Conference Selection */}
              {selectedNCAASport && NCAA_CONFERENCES[selectedNCAASport] && (
                <div className="space-y-3">
                  <Label className="text-base font-medium">Select Conference (Optional)</Label>
                  <Select value={selectedConference} onValueChange={setSelectedConference}>
                    <SelectTrigger className="w-full md:w-[300px]">
                      <SelectValue placeholder="All Conferences (Large sync)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Conferences (Large sync)</SelectItem>
                      {NCAA_CONFERENCES[selectedNCAASport].map((conf) => (
                        <SelectItem key={conf.id} value={conf.id}>
                          {conf.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    💡 Tip: Syncing by conference is faster and more manageable. Each Power 5 conference has ~14-16 teams.
                  </p>
                </div>
              )}
              
              {/* Sync Progress */}
              {ncaaSyncProgress && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <RefreshCw className={`h-4 w-4 ${ncaaSyncing ? 'animate-spin' : ''}`} />
                      {ncaaSyncing ? 'Syncing' : 'Sync Progress'}: {NCAA_SPORTS.find(s => s.key === ncaaSyncProgress.sport)?.label}
                      {ncaaSyncProgress.conference && ` - ${ncaaSyncProgress.conference}`}
                    </h4>
                    <Badge variant={ncaaSyncProgress.hasMore ? 'secondary' : 'default'}>
                      {ncaaSyncProgress.hasMore ? 'In Progress' : 'Complete'}
                    </Badge>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-muted rounded-full h-3">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all duration-300"
                      style={{ width: `${ncaaSyncProgress.progressPercent}%` }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">{ncaaSyncProgress.teamsCount}</p>
                      <p className="text-xs text-muted-foreground">Teams</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{ncaaSyncProgress.totalProcessed}</p>
                      <p className="text-xs text-muted-foreground">Processed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-500">{ncaaSyncProgress.athletesImported}</p>
                      <p className="text-xs text-muted-foreground">Imported</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{ncaaSyncProgress.totalAthletes}</p>
                      <p className="text-xs text-muted-foreground">Total Athletes</p>
                    </div>
                  </div>
                  
                  {ncaaSyncProgress.hasMore && !ncaaSyncing && (
                    <Button 
                      onClick={() => handleNCAASportsSync(ncaaSyncProgress.sport, selectedConference, true)}
                      className="w-full"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Continue Sync ({ncaaSyncProgress.totalAthletes - ncaaSyncProgress.totalProcessed} remaining)
                    </Button>
                  )}
                </div>
              )}
              
              {/* Sync Button */}
              {selectedNCAASport && (
                <Button 
                  onClick={() => handleNCAASportsSync(selectedNCAASport, selectedConference, false)}
                  disabled={ncaaSyncing}
                  className="w-full"
                  size="lg"
                >
                  {ncaaSyncing ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Sync {NCAA_SPORTS.find(s => s.key === selectedNCAASport)?.label} Athletes
                      {selectedConference && selectedConference !== 'all' && ` (${NCAA_CONFERENCES[selectedNCAASport]?.find(c => c.id === selectedConference)?.name})`}
                    </>
                  )}
                </Button>
              )}
              
              {/* Info Box */}
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <h4 className="font-medium mb-2">About NCAA Sports Sync</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Batch Processing:</strong> Syncs 500 athletes per batch for faster syncing</li>
                  <li>• <strong>Conference Filtering:</strong> Filter by major conferences like SEC, Big Ten, ACC</li>
                  <li>• <strong>Division 1 Athletes:</strong> Syncs D1 athletes from football, basketball, baseball, and softball</li>
                  <li>• Free ESPN API - no API key required</li>
                  <li>• Athletes are marked as collegiate with conference information</li>
                </ul>
              </div>
              
              {/* Estimated Counts */}
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <h4 className="font-medium mb-2">Estimated Athlete Counts</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span>College Football (FBS)</span>
                    <span className="font-medium">~11,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Men's Basketball</span>
                    <span className="font-medium">~5,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Women's Basketball</span>
                    <span className="font-medium">~5,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>College Baseball</span>
                    <span className="font-medium">~10,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>College Softball</span>
                    <span className="font-medium">~6,000</span>
                  </div>
                  <div className="flex justify-between font-medium text-primary">
                    <span>Per Conference</span>
                    <span>~1,000-1,500</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Creators Tab */}
        <TabsContent value="creators" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Creators Database
                  </CardTitle>
                  <CardDescription>
                    Browse {creatorStats?.totalGroups.toLocaleString() || 0} creator profiles ({creatorStats?.totalCreators.toLocaleString() || 0} accounts) across platforms
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => setIsAddSingleCreatorOpen(true)} variant="outline" size="sm">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add Creator
                  </Button>
                  <Button onClick={() => setIsCreatorImportModalOpen(true)} variant="outline" size="sm">
                    <Upload className="mr-2 h-4 w-4" />
                    Import Excel
                  </Button>
                  <Button 
                    variant={showSearchSync ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setShowSearchSync(!showSearchSync)}
                  >
                    <Search className="mr-2 h-4 w-4" />
                    Search & Sync
                  </Button>
                  <Button
                    variant={isCreatorEditMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setIsCreatorEditMode(!isCreatorEditMode);
                      if (isCreatorEditMode) setSelectedDbCreators(new Set());
                    }}
                  >
                    {isCreatorEditMode ? <X className="h-4 w-4 mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
                    {isCreatorEditMode ? 'Cancel' : 'Edit'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                {/* Search, Filters, and View Toggle */}
                <div className="flex gap-3 flex-wrap items-center">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search creators by name or username..."
                      value={creatorDbSearch}
                      onChange={(e) => setCreatorDbSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  <Select value={creatorDbClaimFilter} onValueChange={setCreatorDbClaimFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Claim Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="unclaimed">Unclaimed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Claimed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={creatorDbLinkFilter} onValueChange={setCreatorDbLinkFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Link Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Links</SelectItem>
                      <SelectItem value="linked">Linked ({creatorStats?.byLinkStatus?.linked || 0})</SelectItem>
                      <SelectItem value="unlinked">Unlinked ({creatorStats?.byLinkStatus?.unlinked || 0})</SelectItem>
                    </SelectContent>
                  </Select>

                  <ViewToggle view={creatorViewType} onViewChange={setCreatorViewType} />
                </div>
              </div>

              {/* Link Unlinked Button */}
              {!isCreatorEditMode && ((creatorStats?.byLinkStatus?.unlinked || 0) > 0 || isLinkingCreators) && (
                <div className="space-y-3">
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-3">
                      <button
                        onClick={() => setShowUnlinkedList(!showUnlinkedList)}
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                      >
                        <Unlink className="h-4 w-4 text-orange-500" />
                        <span className="text-sm">
                          <strong>{creatorStats?.byLinkStatus?.unlinked || 0}</strong> imported creators need to be linked
                        </span>
                        {showUnlinkedList ? (
                          <ChevronUp className="h-4 w-4 text-orange-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-orange-500" />
                        )}
                      </button>
                      <Button
                        onClick={handleLinkUnlinkedCreators}
                        disabled={isLinkingCreators}
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        {isLinkingCreators ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Linking...
                          </>
                        ) : (
                          <>
                            <Link className="h-4 w-4 mr-2" />
                            Link Unlinked Accounts
                          </>
                        )}
                      </Button>
                    </div>

                    {showUnlinkedList && (
                      <div className="border-t border-orange-500/20">
                        {loadingUnlinkedList ? (
                          <div className="p-3 text-center text-sm text-muted-foreground">
                            <RefreshCw className="h-4 w-4 animate-spin inline mr-2" />
                            Loading unlinked accounts...
                          </div>
                        ) : unlinkedCreatorsList.length === 0 ? (
                          <div className="p-3 text-center text-sm text-muted-foreground">
                            No unlinked accounts found
                          </div>
                        ) : (
                          <ScrollArea className="max-h-48">
                            <div className="divide-y divide-orange-500/10">
                              {unlinkedCreatorsList.map((creator) => {
                                const platformColors: Record<string, string> = {
                                  tiktok: 'bg-pink-500/20 text-pink-400',
                                  instagram: 'bg-purple-500/20 text-purple-400',
                                  youtube: 'bg-red-500/20 text-red-400',
                                };
                                const platformLabels: Record<string, string> = {
                                  tiktok: 'TK',
                                  instagram: 'IG',
                                  youtube: 'YT',
                                };
                                return (
                                  <div key={creator.id} className="flex items-center gap-2 px-3 py-2">
                                    <Avatar className="h-6 w-6">
                                      <AvatarImage src={creator.avatar_url || ''} />
                                      <AvatarFallback className="text-[10px]">
                                        {(creator.display_name || creator.username || '?')[0]?.toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${platformColors[creator.platform] || ''}`}>
                                      {platformLabels[creator.platform] || creator.platform?.toUpperCase()}
                                    </Badge>
                                    <span className="text-sm truncate">
                                      @{creator.username}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        )}
                      </div>
                    )}
                  </div>

                  {isLinkingCreators && linkingProgress && (
                    <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg space-y-3">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin text-orange-500" />
                        <span className="text-sm font-medium">
                          Linking creators... ({linkingProgress.linked} linked, batch {linkingProgress.currentBatch}/{linkingProgress.totalBatches})
                        </span>
                      </div>
                      <Progress value={linkingProgress.percent} className="h-2" />
                      <p className="text-xs text-muted-foreground">
                        {linkingProgress.failed > 0 && `${linkingProgress.failed} failed. `}
                        {linkingProgress.notFound > 0 && `${linkingProgress.notFound} not found. `}
                        {linkingProgress.percent < 100 ? 'Processing...' : 'Complete!'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Re-sync Avatars Button */}
              {!isCreatorEditMode && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="h-4 w-4 text-blue-500" />
                      <span className="text-sm">Re-sync creator avatars to permanent storage</span>
                    </div>
                    <Button
                      onClick={async () => {
                        setResyncingAvatars(true);
                        try {
                          const { data, error } = await supabase.functions.invoke('resync-creator-avatars');
                          if (error) throw error;
                          toast({
                            title: 'Avatar Sync Complete',
                            description: `Synced: ${data?.synced || 0}, Skipped: ${data?.skipped || 0}, Failed: ${data?.failed || 0}`,
                          });
                          refetchDbCreators();
                        } catch (err) {
                          toast({ title: 'Sync Failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
                        } finally {
                          setResyncingAvatars(false);
                        }
                      }}
                      disabled={resyncingAvatars}
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      {resyncingAvatars ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Re-sync Avatars
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Delete All Button */}
              {!isCreatorEditMode && (
                <div className="flex justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deletingAllCreators}
                    onClick={() => setDeleteAllCreatorsDialogOpen(true)}
                  >
                    {deletingAllCreators && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete All Creators ({creatorStats?.totalGroups || 0})
                  </Button>
                </div>
              )}

              {/* Bulk Delete Toolbar */}
              {isCreatorEditMode && (
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg flex-wrap gap-2">
                  <div className="flex items-center gap-4 flex-wrap">
                    <Checkbox 
                      checked={dbCreatorGroups.length > 0 && selectedDbCreators.size === dbCreatorGroups.length}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedDbCreators(new Set(dbCreatorGroups.map(g => g.id)));
                        } else {
                          setSelectedDbCreators(new Set());
                        }
                      }}
                    />
                    <span className="text-sm font-medium">
                      {selectedDbCreators.size} selected
                      {selectedDbCreators.size < dbCreatorsTotal && ` of ${dbCreatorsTotal} total`}
                    </span>
                    
                    {selectedDbCreators.size > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedDbCreators(new Set())}
                      >
                        Clear Selection
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={selectedDbCreators.size === 0 || deletingCreators}
                    onClick={() => setCreatorBulkDeleteDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedDbCreators.size.toLocaleString()})
                  </Button>
                </div>
              )}

              {/* Creator Views */}
              {dbCreatorsLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading creators...</div>
              ) : dbCreatorGroups.length > 0 ? (
                <>
                  {/* Grid View */}
                  {creatorViewType === 'grid' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {dbCreatorGroups.map((group) => {
                        const isExpanded = expandedCreatorGroups.has(group.id);
                        const formatFollowers = (count: number) => {
                          if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
                          if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
                          return count.toString();
                        };
                        
                        return (
                          <Card 
                            key={group.id} 
                            className={`hover:shadow-md transition-all ${isCreatorEditMode ? 'cursor-pointer' : ''} ${selectedDbCreators.has(group.id) ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                            onClick={() => {
                              if (isCreatorEditMode) {
                                const newSelected = new Set(selectedDbCreators);
                                if (newSelected.has(group.id)) {
                                  newSelected.delete(group.id);
                                } else {
                                  newSelected.add(group.id);
                                }
                                setSelectedDbCreators(newSelected);
                              }
                            }}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                {isCreatorEditMode && (
                                  <Checkbox 
                                    checked={selectedDbCreators.has(group.id)}
                                    onCheckedChange={() => {}}
                                    className="mt-1"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                                <Avatar className="h-12 w-12">
                                  <AvatarImage src={getProxiedImageUrl(group.avatar_url || '')} />
                                  <AvatarFallback>
                                    {group.primary_name?.[0] || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <h4 className="font-medium truncate">{group.display_name || group.primary_name}</h4>
                                      {group.is_verified && (
                                        <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                                      )}
                                    </div>
                                    {!isCreatorEditMode && (
                                      <div className="flex items-center gap-1 shrink-0">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCreatorToEdit(group);
                                          }}
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCreatorToDelete({ 
                                              id: group.id, 
                                              name: group.display_name || group.primary_name,
                                              isGrouped: group.is_grouped 
                                            });
                                          }}
                                          disabled={deletingCreator}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Platform Badges */}
                                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                    {group.platforms.map((platform) => (
                                      <Badge 
                                        key={platform}
                                        variant="secondary" 
                                        className={`text-xs ${
                                          platform === 'tiktok' ? 'bg-[#00f2ea]/10 text-[#00f2ea]' :
                                          platform === 'instagram' ? 'bg-[#fd1d1d]/10 text-[#fd1d1d]' :
                                          'bg-[#ff0000]/10 text-[#ff0000]'
                                        }`}
                                      >
                                        {platform}
                                      </Badge>
                                    ))}
                                    {group.claim_status && group.claim_status !== 'unclaimed' && (
                                      <Badge variant={group.claim_status === 'approved' ? 'default' : 'secondary'} className="text-xs">
                                        {group.claim_status === 'approved' ? 'Claimed' : 'Pending'}
                                      </Badge>
                                    )}
                                    {!group.is_fully_linked && (
                                      <Badge className="text-xs bg-orange-500/10 text-orange-500 border-orange-500/20">
                                        <Unlink className="h-3 w-3 mr-1" />
                                        {group.unlinked_count > 1 ? `${group.unlinked_count} Unlinked` : 'Unlinked'}
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* Total Followers */}
                                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                    <span className="font-medium text-foreground">
                                      {formatFollowers(group.total_followers)} total followers
                                    </span>
                                  </div>
                                  
                                  {/* Expandable Linked Accounts */}
                                  {group.creators.length > 1 && (
                                    <div className="mt-2">
                                      <button
                                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newExpanded = new Set(expandedCreatorGroups);
                                          if (isExpanded) {
                                            newExpanded.delete(group.id);
                                          } else {
                                            newExpanded.add(group.id);
                                          }
                                          setExpandedCreatorGroups(newExpanded);
                                        }}
                                      >
                                        {isExpanded ? '▼' : '▶'} {group.creators.length} linked accounts
                                      </button>
                                      {isExpanded && (
                                        <div className="mt-2 space-y-1.5 text-xs border-l-2 border-muted pl-2">
                                          {group.creators.map((creator) => (
                                            <div key={creator.id} className="flex items-center justify-between gap-2">
                                              <div className="flex items-center gap-1.5">
                                                <Badge 
                                                  variant="secondary" 
                                                  className={`text-[10px] px-1 py-0 ${
                                                    creator.platform === 'tiktok' ? 'bg-[#00f2ea]/10 text-[#00f2ea]' :
                                                    creator.platform === 'instagram' ? 'bg-[#fd1d1d]/10 text-[#fd1d1d]' :
                                                    'bg-[#ff0000]/10 text-[#ff0000]'
                                                  }`}
                                                >
                                                  {creator.platform.slice(0, 2).toUpperCase()}
                                                </Badge>
                                                <span className="text-muted-foreground">@{creator.username}</span>
                                              </div>
                                              <span className="text-muted-foreground">
                                                {formatFollowers(creator.follower_count || 0)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Single account - show username */}
                                  {group.creators.length === 1 && (
                                    <p className="text-xs text-muted-foreground mt-1">@{group.creators[0].username}</p>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  {/* List View */}
                  {creatorViewType === 'list' && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {isCreatorEditMode && <TableHead className="w-[40px]"></TableHead>}
                          <TableHead className="w-[60px]"></TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Platforms</TableHead>
                          <TableHead>Followers</TableHead>
                          <TableHead>Status</TableHead>
                          {!isCreatorEditMode && <TableHead className="w-[80px]"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dbCreatorGroups.map((group) => {
                          const formatFollowers = (count: number) => {
                            if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
                            if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
                            return count.toString();
                          };
                          return (
                            <TableRow 
                              key={group.id} 
                              className={`${isCreatorEditMode ? 'cursor-pointer' : ''} ${selectedDbCreators.has(group.id) ? 'bg-primary/5' : ''}`}
                              onClick={() => {
                                if (isCreatorEditMode) {
                                  const newSelected = new Set(selectedDbCreators);
                                  if (newSelected.has(group.id)) newSelected.delete(group.id);
                                  else newSelected.add(group.id);
                                  setSelectedDbCreators(newSelected);
                                }
                              }}
                            >
                              {isCreatorEditMode && (
                                <TableCell>
                                  <Checkbox checked={selectedDbCreators.has(group.id)} onCheckedChange={() => {}} onClick={(e) => e.stopPropagation()} />
                                </TableCell>
                              )}
                              <TableCell>
                                <Avatar className="h-10 w-10">
                                  <AvatarImage src={getProxiedImageUrl(group.avatar_url || '')} className="object-cover" />
                                  <AvatarFallback>{group.primary_name?.[0] || '?'}</AvatarFallback>
                                </Avatar>
                              </TableCell>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  {group.display_name || group.primary_name}
                                  {group.is_verified && <CheckCircle className="h-4 w-4 text-primary" />}
                                </div>
                                {group.creators.length === 1 && (
                                  <p className="text-xs text-muted-foreground">@{group.creators[0].username}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {group.platforms.map(p => (
                                    <Badge key={p} variant="outline" className="text-xs capitalize">{p}</Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>{formatFollowers(group.total_followers)}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {!group.is_fully_linked && (
                                    <Badge className="text-xs bg-orange-500/10 text-orange-500 border-orange-500/20">
                                      Unlinked
                                    </Badge>
                                  )}
                                  {group.claim_status !== 'unclaimed' && (
                                    <Badge variant={group.claim_status === 'approved' ? 'default' : 'secondary'} className="text-xs">
                                      {group.claim_status === 'approved' ? 'Claimed' : 'Pending'}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              {!isCreatorEditMode && (
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setCreatorToEdit(group); }}>
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-destructive" onClick={(e) => { e.stopPropagation(); setCreatorToDelete({ id: group.id, name: group.display_name || group.primary_name, isGrouped: group.is_grouped }); }} disabled={deletingCreator}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}

                  {/* Compact View */}
                  {creatorViewType === 'compact' && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                      {dbCreatorGroups.map((group) => (
                        <div
                          key={group.id}
                          className={`flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors ${selectedDbCreators.has(group.id) ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                          onClick={() => {
                            if (isCreatorEditMode) {
                              const newSelected = new Set(selectedDbCreators);
                              if (newSelected.has(group.id)) newSelected.delete(group.id);
                              else newSelected.add(group.id);
                              setSelectedDbCreators(newSelected);
                            } else {
                              setCreatorToEdit(group);
                            }
                          }}
                        >
                          {isCreatorEditMode && (
                            <Checkbox checked={selectedDbCreators.has(group.id)} onCheckedChange={() => {}} onClick={(e) => e.stopPropagation()} />
                          )}
                          <Avatar className="h-16 w-16">
                            <AvatarImage src={getProxiedImageUrl(group.avatar_url || '')} className="object-cover" />
                            <AvatarFallback className="bg-gradient-to-br from-muted to-muted/60">{group.primary_name?.[0] || '?'}</AvatarFallback>
                          </Avatar>
                          <div className="text-center min-w-0 w-full">
                            <p className="text-sm font-medium truncate flex items-center justify-center gap-1">
                              {group.display_name || group.primary_name}
                              {group.is_verified && <CheckCircle className="h-3 w-3 text-primary shrink-0" />}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {group.platforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' • ')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No creators found. Use Search & Sync to add creators from social platforms.
                </div>
              )}

              {/* Pagination */}
              {dbCreatorsTotal > 20 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {creatorDbPage * 20 + 1} - {Math.min((creatorDbPage + 1) * 20, dbCreatorsTotal)} of {dbCreatorsTotal.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCreatorDbPage(Math.max(0, creatorDbPage - 1))}
                      disabled={creatorDbPage === 0}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {creatorDbPage + 1} of {Math.ceil(dbCreatorsTotal / 20)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCreatorDbPage(creatorDbPage + 1)}
                      disabled={(creatorDbPage + 1) * 20 >= dbCreatorsTotal}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Collapsible Search & Sync Section */}
              <Collapsible open={showSearchSync} onOpenChange={setShowSearchSync}>
                <CollapsibleContent className="space-y-4 pt-4 border-t border-border">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Video className="h-5 w-5" />
                    Search & Sync New Creators
                  </h3>
                  
                  {/* Platform Selection */}
                  <div className="space-y-2">
                    <Label>Platform</Label>
                    <Select value={creatorsPlatform} onValueChange={(value: 'tiktok' | 'instagram' | 'youtube') => setCreatorsPlatform(value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Search Input */}
                  <div className="space-y-2">
                    <Label>Search Query</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search creators by name or username..."
                        value={creatorsSearchQuery}
                        onChange={(e) => setCreatorsSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreatorsSearch()}
                        className="flex-1"
                      />
                      <Button 
                        onClick={handleCreatorsSearch} 
                        disabled={isSearchingCreators || !creatorsSearchQuery.trim()}
                      >
                        {isSearchingCreators ? (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            Searching...
                          </>
                        ) : (
                          <>
                            <Search className="mr-2 h-4 w-4" />
                            Search
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Results Grid */}
                  {creatorsResults.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <h4 className="font-medium">Search Results ({creatorsResults.length})</h4>
                        <div className="flex flex-wrap items-center gap-4">
                          {creatorsPlatform === 'tiktok' && (
                            <div className="flex items-center gap-2">
                              <Switch 
                                id="fetch-demographics"
                                checked={fetchDemographics}
                                onCheckedChange={setFetchDemographics}
                              />
                              <Label htmlFor="fetch-demographics" className="text-sm text-muted-foreground">
                                Fetch demographics (26 credits/creator)
                              </Label>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (selectedCreatorsToSync.size === creatorsResults.length) {
                                  setSelectedCreatorsToSync(new Set());
                                } else {
                                  setSelectedCreatorsToSync(new Set(creatorsResults.map(c => c.id)));
                                }
                              }}
                            >
                              {selectedCreatorsToSync.size === creatorsResults.length ? 'Deselect All' : 'Select All'}
                            </Button>
                            <Button
                              onClick={handleSyncCreators}
                              disabled={isSyncingCreators || selectedCreatorsToSync.size === 0}
                            >
                              {isSyncingCreators ? (
                                <>
                                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                  {syncProgress ? `Syncing ${syncProgress.current}/${syncProgress.total}...` : 'Syncing...'}
                                </>
                              ) : (
                                <>
                                  <Database className="mr-2 h-4 w-4" />
                                  Sync Selected ({selectedCreatorsToSync.size})
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {syncProgress && (
                        <div className="space-y-2">
                          <Progress value={(syncProgress.current / syncProgress.total) * 100} className="h-2" />
                          <p className="text-xs text-muted-foreground text-center">
                            Fetching full profile data for {syncProgress.current} of {syncProgress.total} creators...
                          </p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {creatorsResults.map((creator) => (
                          <Card 
                            key={creator.id} 
                            className={`cursor-pointer transition-all ${
                              selectedCreatorsToSync.has(creator.id) 
                                ? 'ring-2 ring-primary bg-primary/5' 
                                : 'hover:shadow-md'
                            }`}
                            onClick={() => {
                              const newSet = new Set(selectedCreatorsToSync);
                              if (newSet.has(creator.id)) {
                                newSet.delete(creator.id);
                              } else {
                                newSet.add(creator.id);
                              }
                              setSelectedCreatorsToSync(newSet);
                            }}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <Checkbox 
                                  checked={selectedCreatorsToSync.has(creator.id)}
                                  className="mt-1"
                                />
                                <Avatar className="h-12 w-12">
                                  <AvatarImage src={getProxiedImageUrl(creator.avatar_url)} />
                                  <AvatarFallback>{creator.display_name?.[0] || creator.username[0]}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium truncate">{creator.display_name || creator.username}</h4>
                                    {creator.is_verified && (
                                      <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">@{creator.username}</p>
                                  <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                                    <span>{creator.follower_count >= 1000000 
                                      ? `${(creator.follower_count / 1000000).toFixed(1)}M`
                                      : creator.follower_count >= 1000 
                                        ? `${(creator.follower_count / 1000).toFixed(0)}K`
                                        : creator.follower_count} followers</span>
                                    <span>{creator.video_count} videos</span>
                                  </div>
                                  <Badge 
                                    variant="secondary" 
                                    className={`mt-2 text-xs ${
                                      creator.platform === 'tiktok' ? 'bg-[#00f2ea]/10 text-[#00f2ea]' :
                                      creator.platform === 'instagram' ? 'bg-[#fd1d1d]/10 text-[#fd1d1d]' :
                                      'bg-[#ff0000]/10 text-[#ff0000]'
                                    }`}
                                  >
                                    {creator.platform}
                                  </Badge>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Info Box */}
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <h4 className="font-medium mb-2">About Creators Sync</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Search for creators across TikTok, Instagram, and YouTube</li>
                      <li>• Select creators and sync them to the database for the marketplace</li>
                      <li>• Synced creators can be claimed by users</li>
                      <li>• Creator data includes follower count, video count, and verification status</li>
                    </ul>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>
        </TabsContent>
  

        {/* ESPN Athletes Tab */}
        <TabsContent value="espn-athletes" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-500" />
                    ESPN Athletes Database
                  </CardTitle>
                  <CardDescription>
                    Browse all {espnAthleteStats?.totalAthletes.toLocaleString() || 0} ESPN athletes across {Object.keys(espnAthleteStats?.bySport || {}).length} sports
                  </CardDescription>
                </div>
                <Button
                  variant={isEspnEditMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setIsEspnEditMode(!isEspnEditMode);
                    if (isEspnEditMode) setSelectedEspnAthletes(new Set());
                  }}
                >
                  {isEspnEditMode ? <X className="h-4 w-4 mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
                  {isEspnEditMode ? 'Cancel' : 'Edit'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filter Bar */}
              <div className="flex flex-col gap-4">
                {/* Sport Category Buttons */}
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant={espnAthleteSportFilter === '' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setEspnAthleteSportFilter('')}
                  >
                    All Sports
                  </Button>
                  {Object.entries(espnAthleteStats?.bySport || {}).map(([sport, stats]) => (
                    <Button
                      key={sport}
                      variant={espnAthleteSportFilter === sport ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setEspnAthleteSportFilter(sport)}
                      className="capitalize"
                    >
                      {sport} ({stats.total})
                    </Button>
                  ))}
                </div>

                {/* Search and Claim Status Filter */}
                <div className="flex gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search ESPN athletes by name..."
                      value={espnAthleteSearch}
                      onChange={(e) => setEspnAthleteSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  <Select value={espnAthleteClaimFilter} onValueChange={setEspnAthleteClaimFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Claim Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="unclaimed">Unclaimed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Claimed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={espnAthleteTeamFilter} onValueChange={setEspnAthleteTeamFilter}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="All Teams" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Teams</SelectItem>
                      {espnUniqueTeams.map((team) => (
                        <SelectItem key={team} value={team}>
                          {team}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Delete All Button - Visible when not in edit mode */}
              {!isEspnEditMode && (
                <div className="flex justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deletingAllEspnAthletes}
                    onClick={() => setDeleteAllEspnDialogOpen(true)}
                  >
                    {deletingAllEspnAthletes && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete All {espnAthleteSportFilter ? espnAthleteSportFilter.toUpperCase() : ''} Athletes 
                    ({espnAthleteSportFilter 
                      ? (espnAthleteStats?.bySport?.[espnAthleteSportFilter.toLowerCase()]?.total || 0).toLocaleString()
                      : (espnAthleteStats?.totalAthletes || 0).toLocaleString()
                    })
                  </Button>
                </div>
              )}

              {/* Bulk Delete Toolbar */}
              {isEspnEditMode && (
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg flex-wrap gap-2">
                  <div className="flex items-center gap-4 flex-wrap">
                    <Checkbox 
                      checked={espnAthletes.length > 0 && selectedEspnAthletes.size === espnAthletes.length}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedEspnAthletes(new Set(espnAthletes.map(a => a.id)));
                        } else {
                          setSelectedEspnAthletes(new Set());
                        }
                      }}
                    />
                    <span className="text-sm font-medium">
                      {selectedEspnAthletes.size} selected
                      {selectedEspnAthletes.size < espnAthletesTotal && ` of ${espnAthletesTotal} total`}
                    </span>
                    
                    {selectedEspnAthletes.size > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedEspnAthletes(new Set())}
                      >
                        Clear Selection
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={selectedEspnAthletes.size === 0 || deletingEspnAthletes}
                    onClick={() => setEspnBulkDeleteDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedEspnAthletes.size.toLocaleString()})
                  </Button>
                </div>
              )}

              {/* ESPN Athletes Grid */}
              {espnAthletesLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading ESPN athletes...</div>
              ) : espnAthletes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {espnAthletes.map((athlete) => (
                    <Card 
                      key={athlete.id} 
                      className={`hover:shadow-md transition-all ${isEspnEditMode ? 'cursor-pointer' : ''} ${selectedEspnAthletes.has(athlete.id) ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                      onClick={() => {
                        if (isEspnEditMode) {
                          const newSelected = new Set(selectedEspnAthletes);
                          if (newSelected.has(athlete.id)) {
                            newSelected.delete(athlete.id);
                          } else {
                            newSelected.add(athlete.id);
                          }
                          setSelectedEspnAthletes(newSelected);
                        }
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {isEspnEditMode && (
                            <Checkbox 
                              checked={selectedEspnAthletes.has(athlete.id)}
                              onCheckedChange={() => {}}
                              className="mt-1"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={athlete.headshot_url || undefined} />
                            <AvatarFallback className="text-sm">
                              {athlete.first_name?.[0] || ''}{athlete.last_name?.[0] || athlete.name?.[0] || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-medium truncate">{athlete.display_name || athlete.name}</h4>
                              {!isEspnEditMode && (
                                <div className="flex items-center gap-1 shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEspnAthleteToDelete({ id: athlete.id, name: athlete.display_name || athlete.name });
                                    }}
                                    disabled={deletingEspnAthlete}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {athlete.current_team_name || 'No team'}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant="secondary" className="text-xs capitalize">
                                {athlete.sport}
                              </Badge>
                              {athlete.position && (
                                <Badge variant="outline" className="text-xs">
                                  {athlete.position}
                                </Badge>
                              )}
                              {athlete.jersey_number && (
                                <Badge variant="outline" className="text-xs">
                                  #{athlete.jersey_number}
                                </Badge>
                              )}
                            </div>
                            {/* Physical stats row */}
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                              {athlete.age && (
                                <span>Age: {athlete.age}</span>
                              )}
                              {athlete.height && (
                                <span>H: {athlete.height}</span>
                              )}
                              {athlete.weight && (
                                <span>W: {athlete.weight}</span>
                              )}
                            </div>
                            {/* Draft & College info */}
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground flex-wrap">
                              {athlete.college && (
                                <span className="flex items-center gap-1">
                                  <GraduationCap className="h-3 w-3" />
                                  {athlete.college}
                                </span>
                              )}
                              {athlete.draft_year && (
                                <span>Draft: {athlete.draft_year} R{athlete.draft_round} #{athlete.draft_number}</span>
                              )}
                              {athlete.years_experience && (
                                <span>{athlete.years_experience}yr exp</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              {athlete.claim_status === 'approved' && (
                                <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-xs">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Claimed
                                </Badge>
                              )}
                              {athlete.claim_status === 'pending' && (
                                <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20 text-xs">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                              {(!athlete.claim_status || athlete.claim_status === 'unclaimed') && (
                                <Badge variant="outline" className="text-xs">Unclaimed</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No ESPN athletes found. Try adjusting your filters or sync some athletes from the ESPN Marketplace tab.
                </div>
              )}

              {/* Pagination */}
              {espnAthletesTotal > 0 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {Math.min(espnAthletePage * 20 + 1, espnAthletesTotal)} - {Math.min((espnAthletePage + 1) * 20, espnAthletesTotal)} of {espnAthletesTotal} athletes
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={espnAthletePage === 0}
                      onClick={() => setEspnAthletePage(p => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(espnAthletePage + 1) * 20 >= espnAthletesTotal}
                      onClick={() => setEspnAthletePage(p => p + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Claims Tab */}
        <TabsContent value="claims">
          <div className="space-y-6">
            {/* Athlete Claim Requests */}
            <Card>
              <CardHeader>
                <CardTitle>Athlete Claim Requests</CardTitle>
                <CardDescription>Review and approve athlete verification claims</CardDescription>
                <Button variant="outline" size="sm" onClick={() => refetchClaims()}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {claimsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : claimRequests && claimRequests.length > 0 ? (
                  <div className="space-y-4">
                    {claimRequests.map((claim) => (
                      <Card key={claim.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={claim.api_athletes?.photo_url} />
                              <AvatarFallback>{claim.api_athletes ? getAthleteInitials(claim.api_athletes) : '?'}</AvatarFallback>
                            </Avatar>
                            
                            <div className="flex-1 space-y-2">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-semibold">{claim.api_athletes ? getAthleteFullName(claim.api_athletes) : 'Unknown'}</h3>
                                  <p className="text-sm text-muted-foreground capitalize">
                                    {claim.api_athletes?.sport} • {claim.api_athletes?.current_team_name}
                                  </p>
                                </div>
                                {getStatusBadge(claim.status)}
                              </div>

                              <div className="flex items-center gap-2 text-sm">
                                <Avatar className="h-6 w-6">
                                  <AvatarImage src={claim.profiles?.avatar_url} />
                                  <AvatarFallback>{claim.profiles?.display_name?.[0]}</AvatarFallback>
                                </Avatar>
                                <span className="font-medium">{claim.profiles?.display_name}</span>
                                <span className="text-muted-foreground">({claim.profiles?.email})</span>
                              </div>

                              {claim.verification_notes && (
                                <p className="text-sm bg-muted p-2 rounded">
                                  {claim.verification_notes}
                                </p>
                              )}

                              {claim.verification_proof_url && (
                                <div className="text-sm">
                                  <a href={claim.verification_proof_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    View Verification Proof — <span className="text-muted-foreground truncate max-w-[300px] inline-block align-bottom">{claim.verification_proof_url}</span>
                                  </a>
                                </div>
                              )}

                              {claim.status === 'pending' && (
                                <div className="flex gap-2 pt-2">
                                  <Button
                                    size="sm"
                                    onClick={() => setReviewingClaim({ ...claim, _claimType: 'athlete' })}
                                    variant="default"
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Review
                                  </Button>
                                </div>
                              )}

                              {claim.status !== 'pending' && claim.admin_notes && (
                                <p className="text-sm text-muted-foreground italic">
                                  Admin: {claim.admin_notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No athlete claim requests
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Creator Claim Requests */}
            <Card>
              <CardHeader>
                <CardTitle>Creator Claim Requests</CardTitle>
                <CardDescription>Review and approve creator profile claims</CardDescription>
                <Button variant="outline" size="sm" onClick={() => refetchCreatorClaims()}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {creatorClaimsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : creatorClaimRequests && creatorClaimRequests.length > 0 ? (
                  <div className="space-y-4">
                    {/* Group creator claims by claimer profile */}
                    {(() => {
                      const grouped = new Map<string, { profile: any; creators: any[] }>();
                      creatorClaimRequests.forEach((creator: any) => {
                        const key = creator.claimed_by_profile_id || creator.id;
                        if (!grouped.has(key)) {
                          grouped.set(key, { profile: creator.claimer_profile, creators: [] });
                        }
                        grouped.get(key)!.creators.push(creator);
                      });
                      return Array.from(grouped.entries()).map(([profileId, { profile, creators }]) => (
                        <Card key={profileId}>
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              {profile && (
                                <Avatar className="h-12 w-12">
                                  <AvatarImage src={profile.avatar_url} />
                                  <AvatarFallback>{profile.display_name?.[0]}</AvatarFallback>
                                </Avatar>
                              )}

                              <div className="flex-1 space-y-3">
                                {profile && (
                                  <div>
                                    <h3 className="font-semibold">{profile.display_name}</h3>
                                    <p className="text-sm text-muted-foreground">{profile.email}</p>
                                  </div>
                                )}

                                {/* Linked accounts */}
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Linked Accounts ({creators.length})</p>
                                  {creators.map((creator: any) => (
                                    <div key={creator.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                                      <Avatar className="h-8 w-8">
                                        <AvatarImage src={creator.avatar_url ? getProxiedImageUrl(creator.avatar_url) : undefined} />
                                        <AvatarFallback>{(creator.display_name || creator.username)?.[0]?.toUpperCase() || '?'}</AvatarFallback>
                                      </Avatar>
                                      <div className="flex-1 min-w-0">
                                        <span className="text-sm font-medium">{creator.display_name || creator.username}</span>
                                        <span className="text-xs text-muted-foreground ml-2">
                                          @{creator.username} • <Badge variant="outline" className="text-xs capitalize">{creator.platform}</Badge>
                                          {creator.primary_platform && <Badge variant="secondary" className="text-xs capitalize ml-1">Primary</Badge>}
                                          {creator.follower_count > 0 && ` • ${creator.follower_count >= 1000000 ? `${(creator.follower_count / 1000000).toFixed(1)}M` : creator.follower_count >= 1000 ? `${(creator.follower_count / 1000).toFixed(0)}K` : creator.follower_count} followers`}
                                        </span>
                                      </div>
                                      {getStatusBadge(creator.claim_status)}
                                    </div>
                                  ))}
                                </div>

                                {creators.some((c: any) => c.claim_status === 'pending') && (
                                  <div className="flex gap-2 pt-2">
                                    <Button
                                      size="sm"
                                      onClick={() => setReviewingClaim({ ...creators[0], _claimType: 'creator', _allCreators: creators })}
                                      variant="default"
                                    >
                                      <CheckCircle className="mr-2 h-4 w-4" />
                                      Review All
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ));
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No creator claim requests
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Review Claim Dialog */}
      <Dialog open={!!reviewingClaim} onOpenChange={() => setReviewingClaim(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review {reviewingClaim?._claimType === 'creator' ? 'Creator' : 'Athlete'} Claim Request</DialogTitle>
            <DialogDescription>
              Review and approve or reject this {reviewingClaim?._claimType === 'creator' ? 'creator' : 'athlete'} profile claim
            </DialogDescription>
          </DialogHeader>

          {reviewingClaim && (
            <div className="space-y-4">
              {reviewingClaim._claimType === 'creator' && reviewingClaim._allCreators ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Linked Accounts ({reviewingClaim._allCreators.length})</p>
                  {reviewingClaim._allCreators.map((creator: any) => (
                    <div key={creator.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={creator.avatar_url ? getProxiedImageUrl(creator.avatar_url) : undefined} />
                        <AvatarFallback>{(creator.display_name || creator.username)?.[0]?.toUpperCase() || '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{creator.display_name || creator.username}</span>
                        <p className="text-xs text-muted-foreground">
                          @{creator.username} • <Badge variant="outline" className="text-xs capitalize">{creator.platform}</Badge>
                          {creator.follower_count > 0 && ` • ${creator.follower_count >= 1000000 ? `${(creator.follower_count / 1000000).toFixed(1)}M` : creator.follower_count >= 1000 ? `${(creator.follower_count / 1000).toFixed(0)}K` : creator.follower_count} followers`}
                        </p>
                      </div>
                      {getStatusBadge(creator.claim_status)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    {reviewingClaim._claimType === 'creator' ? (
                      <>
                        <AvatarImage src={reviewingClaim.avatar_url ? getProxiedImageUrl(reviewingClaim.avatar_url) : undefined} />
                        <AvatarFallback>{(reviewingClaim.display_name || reviewingClaim.username)?.[0]?.toUpperCase() || '?'}</AvatarFallback>
                      </>
                    ) : (
                      <>
                        <AvatarImage src={reviewingClaim.api_athletes?.photo_url} />
                        <AvatarFallback>{reviewingClaim.api_athletes ? getAthleteInitials(reviewingClaim.api_athletes) : '?'}</AvatarFallback>
                      </>
                    )}
                  </Avatar>
                  <div>
                    {reviewingClaim._claimType === 'creator' ? (
                      <>
                        <h3 className="font-semibold text-lg">{reviewingClaim.display_name || reviewingClaim.username}</h3>
                        <p className="text-sm text-muted-foreground">
                          @{reviewingClaim.username} • <Badge variant="outline" className="text-xs capitalize">{reviewingClaim.platform}</Badge>
                          {reviewingClaim.follower_count > 0 && ` • ${(reviewingClaim.follower_count / 1000).toFixed(1)}K followers`}
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="font-semibold text-lg">{reviewingClaim.api_athletes ? getAthleteFullName(reviewingClaim.api_athletes) : 'Unknown'}</h3>
                        <p className="text-sm text-muted-foreground capitalize">
                          {reviewingClaim.api_athletes?.sport} • {reviewingClaim.api_athletes?.current_team_name}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Requested by</Label>
                <div className="flex items-center gap-2 p-2 bg-muted rounded">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={reviewingClaim._claimType === 'creator' ? reviewingClaim.claimer_profile?.avatar_url : reviewingClaim.profiles?.avatar_url} />
                    <AvatarFallback>{(reviewingClaim._claimType === 'creator' ? reviewingClaim.claimer_profile?.display_name : reviewingClaim.profiles?.display_name)?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">{reviewingClaim._claimType === 'creator' ? reviewingClaim.claimer_profile?.display_name : reviewingClaim.profiles?.display_name}</div>
                    <div className="text-sm text-muted-foreground">{reviewingClaim._claimType === 'creator' ? reviewingClaim.claimer_profile?.email : reviewingClaim.profiles?.email}</div>
                  </div>
                </div>
              </div>

              {reviewingClaim.verification_notes && (
                <div className="space-y-2">
                  <Label>Verification Notes</Label>
                  <p className="text-sm bg-muted p-3 rounded">{reviewingClaim.verification_notes}</p>
                </div>
              )}

              {reviewingClaim.verification_proof_url && (
                <div className="space-y-2">
                  <Label>Verification Link</Label>
                  <a href={reviewingClaim.verification_proof_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline p-3 bg-muted rounded">
                    <ExternalLink className="h-4 w-4" />
                    {reviewingClaim.verification_proof_url}
                  </a>
                </div>
              )}

              <div className="space-y-2">
                <Label>Admin Notes (Optional)</Label>
                <Textarea
                  placeholder="Add notes about your decision..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setReviewingClaim(null);
                setAdminNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleApproveClaim(false)}
              disabled={approvingClaim || approvingCreatorClaim}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Reject
            </Button>
            <Button
              onClick={() => handleApproveClaim(true)}
              disabled={approvingClaim || approvingCreatorClaim}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ESPN Delete Athlete Confirmation Dialog */}
      <AlertDialog open={!!espnAthleteToDelete} onOpenChange={() => setEspnAthleteToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete ESPN Athlete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{espnAthleteToDelete?.name}" from the ESPN database? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (espnAthleteToDelete) {
                  deleteEspnAthlete(espnAthleteToDelete.id);
                  setEspnAthleteToDelete(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ESPN Bulk Delete Confirmation Dialog */}
      <AlertDialog open={espnBulkDeleteDialogOpen} onOpenChange={setEspnBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedEspnAthletes.size} ESPN Athletes?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected ESPN athletes from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                deleteEspnAthletes(Array.from(selectedEspnAthletes));
                setSelectedEspnAthletes(new Set());
                setIsEspnEditMode(false);
                setEspnBulkDeleteDialogOpen(false);
              }}
            >
              Delete {selectedEspnAthletes.size} Athletes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All ESPN Athletes Confirmation Dialog */}
      <AlertDialog open={deleteAllEspnDialogOpen} onOpenChange={setDeleteAllEspnDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete All {espnAthleteSportFilter ? espnAthleteSportFilter.toUpperCase() : ''} ESPN Athletes?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {espnAthleteSportFilter 
                ? (espnAthleteStats?.bySport?.[espnAthleteSportFilter.toLowerCase()]?.total || 0).toLocaleString()
                : (espnAthleteStats?.totalAthletes || 0).toLocaleString()
              } ESPN athletes from the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                deleteAllEspnAthletesBySport(espnAthleteSportFilter || undefined);
                setDeleteAllEspnDialogOpen(false);
              }}
            >
              Delete All Athletes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Creator Confirmation Dialog */}
      <AlertDialog open={!!creatorToDelete} onOpenChange={() => setCreatorToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Creator</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{creatorToDelete?.name}" from the database? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (creatorToDelete) {
                  deleteCreatorGroup.mutate({ id: creatorToDelete.id, isGrouped: creatorToDelete.isGrouped });
                  setCreatorToDelete(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Creator Bulk Delete Confirmation Dialog */}
      <AlertDialog open={creatorBulkDeleteDialogOpen} onOpenChange={setCreatorBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedDbCreators.size} Creators?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected creators from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                const groupsToDelete = dbCreatorGroups
                  .filter(g => selectedDbCreators.has(g.id))
                  .map(g => ({ id: g.id, isGrouped: g.is_grouped }));
                bulkDeleteGroups.mutate(groupsToDelete);
                setSelectedDbCreators(new Set());
                setIsCreatorEditMode(false);
                setCreatorBulkDeleteDialogOpen(false);
              }}
            >
              Delete {selectedDbCreators.size} Creators
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Creators Confirmation Dialog */}
      <AlertDialog open={deleteAllCreatorsDialogOpen} onOpenChange={setDeleteAllCreatorsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete All Creators?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete ALL {creatorStats?.totalGroups || 0} creator profiles and all associated accounts (TikTok, Instagram, YouTube) from the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                deleteAllCreators.mutate(undefined);
                setDeleteAllCreatorsDialogOpen(false);
              }}
            >
              Delete All Creators
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Photo Upload Dialog */}
      <AthletePhotoUploadDialog
        open={!!photoUploadAthlete}
        onOpenChange={(open) => !open && setPhotoUploadAthlete(null)}
        athlete={photoUploadAthlete}
        onSuccess={() => {
          setPhotoUploadAthlete(null);
          refetchEspnAthletes();
        }}
      />

      {/* Creator Import Modal */}
      <CreatorImportModal
        isOpen={isCreatorImportModalOpen}
        onClose={() => setIsCreatorImportModalOpen(false)}
        onImportComplete={() => {
          toast({
            title: 'Import complete',
            description: 'Creators have been imported successfully.',
          });
        }}
      />

      {/* Creator Edit Dialog */}
      <CreatorEditDialog
        creator={creatorToEdit}
        open={!!creatorToEdit}
        onOpenChange={(open) => { if (!open) setCreatorToEdit(null); }}
      />

      {/* Add Single Creator Dialog */}
      <CreatorAddDialog
        open={isAddSingleCreatorOpen}
        onOpenChange={setIsAddSingleCreatorOpen}
        onCreatorAdded={() => refetchDbCreators()}
      />
    </div>
  );
};
